import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './FlowLayout.css';
import './SpecificDates.css';

const TIME_BLOCKS = ['8–10 AM', '10–12 PM', '12–4 PM', '5–8 PM'];

function generateNextWeekdays(count) {
  var days = [];
  var date = new Date();
  date.setDate(date.getDate() + 2); // Start from day after tomorrow (48hr buffer)
  while (days.length < count) {
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      days.push(new Date(date));
    }
    date.setDate(date.getDate() + 1);
  }
  return days;
}

function formatDate(date) {
  var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return days[date.getDay()] + ', ' + months[date.getMonth()] + ' ' + date.getDate();
}

function SpecificDates() {
  var navigate = useNavigate();
  var weekdays = generateNextWeekdays(10);
  var [selectedSlots, setSelectedSlots] = useState({});

  var locationName = localStorage.getItem('selectedLocationName') || 'Your selected enrollment center';
  var locationAddress = localStorage.getItem('selectedLocationAddress') || '';
  var mapsUrl = locationAddress ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(locationAddress) : 'https://www.google.com/maps';

  function toggleSlot(dateKey, time) {
    var key = dateKey + '|' + time;
    setSelectedSlots(function(prev) {
      var next = Object.assign({}, prev);
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  }

  var selectedCount = Object.keys(selectedSlots).length;

  function handleSubmit() {
    if (selectedCount === 0) return;
    localStorage.setItem('selectedSlots', JSON.stringify(selectedSlots));
    navigate('/connect');
    window.scrollTo(0, 0);
  }

  return (
    <div className="flow-page flow-page--scroll">
      <Helmet>
        <title>Choose Specific Dates</title>
        <meta name="description" content="Choose specific dates and times for your Global Entry enrollment interview." />
      </Helmet>

      {/* Progress Bar */}
      <div className="progress-bar-container" role="progressbar" aria-valuenow={3} aria-valuemin={1} aria-valuemax={5} aria-label="Step 3 of 5: Availability">
        <div className="progress-bar-header">
          <button className="flow-back" onClick={function() { navigate('/calendar'); window.scrollTo(0, 0); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div className="progress-bar-label">Step 3 of 5: Availability</div>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: '60%' }} />
        </div>
      </div>

      {/* Split layout */}
      <div className="flow-layout">
        {/* Left column */}
        <div className="flow-left">
          <h1 className="flow-heading">
            Choose your specific dates and times.
          </h1>

          <div className="location-highlight" style={{ marginTop: '48px' }}>
            <p className="location-highlight__label">Your selected location</p>
            <h3 className="location-highlight__name">{locationName}</h3>
            {locationAddress && <p className="info-card__text" style={{ marginTop: '8px', marginBottom: '0' }}>{locationAddress}</p>}
            <a
              className="info-card__link"
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-block', marginTop: '16px' }}
            >
              View on Google Maps →
            </a>
          </div>
        </div>

        {/* Right column */}
        <div className="flow-right">
          <div className="panel-card">
            <p className="avail-section-label">Select all the dates and times that work for you.</p>

            {/* Specific dates grid */}
            <div className="specific-grid-wrapper">
              <div className="specific-grid">
                {/* Header row */}
                <div className="specific-grid__row specific-grid__row--header">
                  <div className="specific-grid__date-cell"></div>
                  {TIME_BLOCKS.map(function(time) {
                    return <div key={time} className="specific-grid__time-header">{time}</div>;
                  })}
                </div>

                {/* Date rows */}
                {weekdays.map(function(date) {
                  var dateKey = date.toDateString();
                  return (
                    <div key={dateKey} className="specific-grid__row">
                      <div className="specific-grid__date-cell">
                        <span className="specific-grid__date-label">{formatDate(date)}</span>
                      </div>
                      {TIME_BLOCKS.map(function(time) {
                        var key = dateKey + '|' + time;
                        var isChecked = !!selectedSlots[key];
                        return (
                          <div key={time} className="specific-grid__check-cell">
                            <label className="avail-checkbox" aria-label={formatDate(date) + ' ' + time}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={function() { toggleSlot(dateKey, time); }}
                              />
                              <span className={'avail-checkbox__box' + (isChecked ? ' avail-checkbox__box--checked' : '')}>
                                {isChecked && (
                                  <svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Disclaimer */}
            <p className="avail-disclaimer">Dates within 48 hours are excluded to give you enough planning time.</p>

            {/* Actions */}
            <div className="avail-actions">
              <button className="flow-text-link" onClick={function() { navigate('/calendar'); window.scrollTo(0, 0); }}>
                Use General Availability Instead
              </button>
              <button
                className="flow-btn-primary"
                onClick={handleSubmit}
                disabled={selectedCount === 0}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SpecificDates;
